import { CLADDING_RSI, WEATHER_BARRIER_RSI, VAPOUR_BARRIER_RSI, WOOD_RSIF, AF_BY_SPACING_WOOD } from './constants';
import { calcSteelK } from './steelK';
import {
  AIR_FILM_EXTERIOR_RSI,
  WOOD_STRAP_BEHIND_GWB_RSI,
  INTERIOR_GWB_RSI_PER_MM,
  AIR_FILM_INTERIOR_RSI,
  roundOff,
  CODEBORD_RSI_PER_INCH,
  OSB_RSI_PER_MM,
  BRICK_AIR_SPACE_RSI,
  RAINSCREEN_RSI,
  RAINBARRIER_RSI_PER_INCH,
  getFramingText,
} from '../shared/constants';

// --- NEW: normalize the user selection into printable rows -------------------
const labelForCladding = (cladding, hasBrickAirSpace, t) => {
  switch (cladding) {
    // eslint-disable-next-line max-len
    case 'brickClay102':
      return [
        t('thermal_calculator.walls_above_grade.cladding_brick'),
        hasBrickAirSpace ? '25 mm (1") air space (air cavity, walls, heat flow horizontal)' : null,
      ];
    case 'vinylOverStrap':
      return [t('thermal_calculator.walls_above_grade.cladding_vynil'), null];
    case 'vinylUnvented':
      return [t('thermal_calculator.walls_above_grade.cladding_unvented_vynil'), null];
    case 'hardieOverStrap':
      return [t('thermal_calculator.walls_above_grade.cladding_hardie'), null];
    case 'acrylicStuccoOverStrap':
      return [t('thermal_calculator.walls_above_grade.cladding_stucco'), null];
    default:
      return [null, null];
  }
};

const layerListFromInputs = (state, cont, t, locale) => {
  const {
    cladding,
    hasBrickAirSpace,
    sheathingType,
    osbThickness,
    gwbSheathThickness,
    codebordSheathThickness,
    interiorGWB,
    woodStrapBehindGWB,
    exteriorRainbarrierInches,
    exteriorCodebordInches,
  } = state;

  const { code } = locale || {};

  const rows = [];

  // Exterior air film
  rows.push({
    id: 'film_ext',
    label: t('thermal_calculator.walls_above_grade.exterior_air_film'),
    rsi: AIR_FILM_EXTERIOR_RSI,
  });

  // Cladding + (brick) air space
  const [cladLabel, brickASLabel] = labelForCladding(cladding, hasBrickAirSpace, t);
  if (cladLabel) rows.push({ id: 'cladding', label: cladLabel, rsi: cont.claddingRSI });
  if (brickASLabel && cont.brickAirSpaceRSI > 0)
    rows.push({ id: 'brick_air_gap', label: brickASLabel, rsi: cont.brickAirSpaceRSI });

  // Rainscreen (if any)
  if (cont.rainScreenRSI > 0) {
    rows.push({
      id: 'rainscreen',
      label: t('thermal_calculator.walls_above_grade.rainscreen_rsi'),
      rsi: cont.rainScreenRSI,
    });
  }

  rows.push({ id: 'wb', label: t('thermal_calculator.walls_above_grade.wb'), rsi: 0 });

  // Sheathing pieces (show exactly what the user selected)
  if (sheathingType === 'osb') {
    const sheathingCalc = parseFloat(osbThickness || '0');
    rows.push({
      id: 'osb',
      label: `${t('thermal_calculator.walls_above_grade.osb_exterior_sheathing')} ${new Intl.NumberFormat(code).format(
        sheathingCalc
      )} mm`,
      rsi: OSB_RSI_PER_MM * sheathingCalc,
    });
  }
  if (sheathingType === 'gwb') {
    const sheathingCalc = parseFloat(gwbSheathThickness || '0');
    rows.push({
      id: 'gwbSheath',
      label: `${t('thermal_calculator.walls_above_grade.gwb_exterior')} ${new Intl.NumberFormat(code).format(
        sheathingCalc
      )} mm`,
      rsi: INTERIOR_GWB_RSI_PER_MM * sheathingCalc,
    });
  }
  if (sheathingType === 'codebord') {
    const sheathingCalc = parseFloat(codebordSheathThickness || '0');
    rows.push({
      id: 'codebordSheath',
      label: `${t('thermal_calculator.walls_above_grade.codebord_sheathing_exterior')} ${new Intl.NumberFormat(
        code
      ).format(sheathingCalc)}${t('thermal_calculator.inches')}`,
      rsi: CODEBORD_RSI_PER_INCH * sheathingCalc,
    });
  }

  if (cont.exteriorInsulationRSI > 0) {
    const extLabel =
      state.exteriorInsulationType === 'rainbarrier'
        ? `${t('thermal_calculator.walls_above_grade.exterior_insulation_rainbarrier')} ${new Intl.NumberFormat(
            code
          ).format(exteriorRainbarrierInches)}${t('thermal_calculator.inches')}`
        : `${t('thermal_calculator.walls_above_grade.exterior_insulation_codebord')} ${new Intl.NumberFormat(
            code
          ).format(exteriorCodebordInches)}${t('thermal_calculator.inches')}`;
    rows.push({ id: 'ext_insul', label: extLabel, rsi: cont.exteriorInsulationRSI });
  }

  // Membranes (spec explicitly 0)
  rows.push({ id: 'vb', label: t('thermal_calculator.concrete_foundation_floor.vapour_barrier_rsi'), rsi: 0 });

  // Interior face
  if (woodStrapBehindGWB) {
    rows.push({
      id: 'strapping',
      label: t('thermal_calculator.walls_above_grade.strapping_air_cavity'),
      rsi: WOOD_STRAP_BEHIND_GWB_RSI,
    });
  }
  if (interiorGWB) {
    const calc = parseFloat(interiorGWB || '0');
    rows.push({
      id: 'gwb',
      label: `${t('thermal_calculator.floors_over_unheated_spaces.gypsum_board')} ${new Intl.NumberFormat(code).format(
        calc
      )} mm (1/2${t('thermal_calculator.inches')})`,
      rsi: INTERIOR_GWB_RSI_PER_MM * calc,
    });
  }

  // Interior air film
  rows.push({
    id: 'film_int',
    label: t('thermal_calculator.walls_above_grade.interior_air_film'),
    rsi: AIR_FILM_INTERIOR_RSI,
  });

  return rows;
};

// --- existing code below, we just add `layers` into the returned results ----

export const computeContinuousLayers = state => {
  const {
    cladding,
    hasBrickAirSpace,
    hasRainScreen,
    rainScreenAirGap,
    sheathingType,
    osbThickness,
    gwbSheathThickness,
    codebordSheathThickness,
    hasWeatherBarrier,
    hasVapourBarrier,
    interiorGWB,
    woodStrapBehindGWB,
    hasExteriorInsulation,
    exteriorInsulationType,
    exteriorCodebordInches,
    exteriorRainbarrierInches,
  } = state;

  const claddingRSI = CLADDING_RSI[cladding] ?? 0;
  const brickAirSpaceRSI = cladding === 'brickClay102' ? BRICK_AIR_SPACE_RSI[hasBrickAirSpace ? 'yes' : 'no'] ?? 0 : 0;

  const rainScreenRSI = hasRainScreen ? RAINSCREEN_RSI[rainScreenAirGap] ?? 0 : 0;

  const osbRSI = sheathingType === 'osb' ? OSB_RSI_PER_MM * parseFloat(osbThickness || '0') : 0;

  const gwbSheathRSI = sheathingType === 'gwb' ? INTERIOR_GWB_RSI_PER_MM * parseFloat(gwbSheathThickness || '0') : 0;

  const codebordSheathRSI =
    sheathingType === 'codebord' ? CODEBORD_RSI_PER_INCH * parseFloat(codebordSheathThickness || '0') : 0;

  const sheathingRSI = osbRSI + gwbSheathRSI + codebordSheathRSI;

  const weatherBarrierRSI = hasWeatherBarrier ? WEATHER_BARRIER_RSI : 0;
  const vapourBarrierRSI = hasVapourBarrier ? VAPOUR_BARRIER_RSI : 0;

  const interiorGwbRSI = INTERIOR_GWB_RSI_PER_MM * parseFloat(interiorGWB || '0');
  const strapBehindGwbRSI = woodStrapBehindGWB ? WOOD_STRAP_BEHIND_GWB_RSI : 0;

  // exterior CI (only when sheathing !== codebord)
  let exteriorInsulationRSI = 0;
  if (sheathingType !== 'codebord' && hasExteriorInsulation) {
    if (exteriorInsulationType === 'codebord') {
      exteriorInsulationRSI = CODEBORD_RSI_PER_INCH * parseFloat(exteriorCodebordInches || '0');
    } else if (exteriorInsulationType === 'rainbarrier') {
      exteriorInsulationRSI = RAINBARRIER_RSI_PER_INCH * parseFloat(exteriorRainbarrierInches || '0');
    }
  }

  const continuousTotalRSI =
    AIR_FILM_EXTERIOR_RSI +
    claddingRSI +
    brickAirSpaceRSI +
    rainScreenRSI +
    sheathingRSI +
    weatherBarrierRSI +
    vapourBarrierRSI +
    exteriorInsulationRSI +
    interiorGwbRSI +
    strapBehindGwbRSI +
    AIR_FILM_INTERIOR_RSI;

  // Detect if the first low-perm foam layer is an exterior CI CodeBord layer
  const lowPermAtExterior =
    sheathingType !== 'codebord' &&
    hasExteriorInsulation &&
    exteriorInsulationType === 'codebord' &&
    parseFloat(exteriorCodebordInches || '0') > 0;

  const cavityRSI = parseFloat(state.cavityRSIWood || '0');

  // Everything outside the foam (foam excluded)
  let outboardRSI = AIR_FILM_EXTERIOR_RSI + claddingRSI + rainScreenRSI + brickAirSpaceRSI;
  if (state.sheathingType === 'codebord') {
    outboardRSI += sheathingRSI;
  } else {
    outboardRSI += exteriorInsulationRSI;
  }

  // Everything inside the foam (foam excluded)
  let inboardContinuousRSI = cavityRSI + interiorGwbRSI + strapBehindGwbRSI + AIR_FILM_INTERIOR_RSI;
  if (state.sheathingType !== 'codebord') {
    inboardContinuousRSI += sheathingRSI;
  }

  const outboardInboardRatio = inboardContinuousRSI > 0 ? roundOff(outboardRSI / inboardContinuousRSI) : null;
  return {
    claddingRSI,
    brickAirSpaceRSI,
    rainScreenRSI,
    sheathingRSI,
    weatherBarrierRSI,
    vapourBarrierRSI,
    interiorGwbRSI,
    strapBehindGwbRSI,
    exteriorInsulationRSI,
    continuousTotalRSI,
    // for ratio:
    outboardInboardRatio,
    lowPermAtExterior,
  };
};

const calcEffectiveAssembly = (Af, RSIf, Ac, RSIc, cont) => {
  const RSIparallel = RSIf > 0 && RSIc > 0 ? 1 / (Af / RSIf + Ac / RSIc) : 0;
  const effectiveAssemblyRSI = RSIparallel + cont.continuousTotalRSI;

  return { RSIparallel, effectiveAssemblyRSI };
};

export const calcWood = (state, t, locale) => {
  const Af = AF_BY_SPACING_WOOD[state.spacingWood] ?? 0.215;
  const Ac = Math.max(0, 1 - Af);
  const RSIf = WOOD_RSIF[state.woodSize] ?? 0;
  const RSIc = parseFloat(state.cavityRSIWood || '0');

  const continuousLayers = computeContinuousLayers(state);
  const { RSIparallel, effectiveAssemblyRSI } = calcEffectiveAssembly(Af, RSIf, Ac, RSIc, continuousLayers);

  const centerOfCavityRSI = RSIc + continuousLayers.continuousTotalRSI;
  const installedInsulationRSI =
    state.sheathingType === 'codebord'
      ? RSIc + continuousLayers.sheathingRSI
      : RSIc + continuousLayers.exteriorInsulationRSI;

  // ---- Outboard/Inboard ratio (include the core on the inboard side) ----
  const { outboardInboardRatio } = continuousLayers;

  const framingText = getFramingText({ t });

  // NEW: normalized sets for the PDF/UI
  const layers = {
    continuous: layerListFromInputs(state, continuousLayers, t, locale),
    nonContinuous: [
      {
        id: 'stud_cavity',
        label: `${t('thermal_calculator.walls_above_grade.wood_dimensional_lumber')} ${framingText[state.woodSize]}, ${
          state.spacingWood
        } mm ${t('thermal_calculator.floors_over_unheated_spaces.on_center')}, RSI ${RSIc.toFixed(2)}, R ${(
          RSIc * 5.67826
        ).toFixed(0)} ${t('thermal_calculator.floors_over_unheated_spaces.nominal_insulation')}`,
        rsi: RSIparallel,
        note: `1) ${t('thermal_calculator.walls_above_grade.air_cavity_created_by')}`,
      },
    ],
  };

  return {
    path: 'wood',
    Af,
    Ac,
    RSIf,
    RSIc,
    RSIparallel,
    ...continuousLayers,
    centerOfCavityRSI,
    installedInsulationRSI,
    effectiveAssemblyRSI,
    layers,
    // ratio fields:
    outboardInboardRatio,
  };
};

export const calcSteel = (state, t, locale) => {
  const res = calcSteelK(state, t, locale);
  return { path: 'steel', ...res };
};
